import { memo, useContext, useState } from 'react'
import { Group, Plus } from '@icon-park/react'

import { Api } from '@/modules'
import { useRouting } from '@/hooks'

import { Button, Flex } from '@/components'
import { ManualCard, Modal } from '@/components/derived'

import { useCompanyContext } from '../CompanyDetails.context'
import { getDescription } from '../utils'
import { useObserver } from '@/modules/observer/observer.context'
import { PUBLISHER_PERSONS_TABLE, PublisherPersonsTable } from './components/table/PublisherPersonsTable.component'

import { DialogContext } from '@/modules/dialog/Dialog.context'
import { PublisherPersonsFormModal } from './components/publisherPersonsFormModal/PublisherPersonsFormModal.component'

export type ImportResult = {
  created: number
  skipped: number
  updated: number
  errors: string[]
}

function ImportResultContent({ created, skipped, updated, errors }: ImportResult): JSX.Element {
  return (
    <Flex $type="column" $align="stretch" $gap={12}>
      <Flex $type="row" $align="center" $gap={24}>
        <Flex $type="column" $align="center" $gap={4}>
          <span style={{ fontSize: 28, fontWeight: 500, color: '#1D9E75' }}>{created}</span>
          <span style={{ fontSize: 13, color: '#666' }}>criadas</span>
        </Flex>
        <Flex $type="column" $align="center" $gap={4}>
          <span style={{ fontSize: 28, fontWeight: 500, color: '#185FA5' }}>{updated}</span>
          <span style={{ fontSize: 13, color: '#666' }}>atualizadas</span>
        </Flex>
        <Flex $type="column" $align="center" $gap={4}>
          <span style={{ fontSize: 28, fontWeight: 500, color: '#888' }}>{skipped}</span>
          <span style={{ fontSize: 13, color: '#666' }}>sem alteração</span>
        </Flex>
        {errors.length > 0 && (
          <Flex $type="column" $align="center" $gap={4}>
            <span style={{ fontSize: 28, fontWeight: 500, color: '#D85A30' }}>{errors.length}</span>
            <span style={{ fontSize: 13, color: '#666' }}>erros</span>
          </Flex>
        )}
      </Flex>
      {errors.length > 0 && (
        <Flex $type="column" $align="stretch" $gap={6} style={{ marginTop: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: '#D85A30' }}>Erros encontrados:</span>
          {errors.map((err, i) => (
            <span
              key={i}
              style={{
                fontSize: 12,
                color: '#666',
                padding: '4px 8px',
                background: '#fff5f5',
                borderRadius: 6,
              }}>
              {err}
            </span>
          ))}
        </Flex>
      )}
    </Flex>
  )
}

export const PublisherPersons = memo(Component)

function Component(): JSX.Element | null {
  const { companyID, changeQueryParam } = useRouting()
  const { company, reloadCompany } = useCompanyContext()
  const { sendMessage } = useObserver()
  const dialog = useContext(DialogContext)

  const [excelFile, setExcelFile] = useState<File | null>(null)

  const { isRunning: isImporting, run: importPersons } = Api.Command<ImportResult>(
    Api.Methods.POST,
    `companies/:companyID/publisher-persons/import`,
  )

  function handleImport(): void {
    if (!excelFile) return

    const formData = new FormData()
    formData.append('file', excelFile)

    importPersons(
      {
        input: formData,
        binds: [['companyID', companyID!]],
        onSuccess: (result) => {
          setExcelFile(null)
          sendMessage(PUBLISHER_PERSONS_TABLE)

          dialog?.setData({
            title: 'Resultado da Importação',
            content: <ImportResultContent {...result!} />,
            closeText: 'Fechar',
            customActions: (
              <Button.Root $variant="outlined" onClick={() => dialog?.close()}>
                <Button.Text value="Fechar" />
              </Button.Root>
            ),
            justifiedButtons: true,
          })
          dialog?.open()
        },
      },
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
  }

  if (!companyID || !company) return null

  return (
    <>
      {company.motherID ?
        <ManualCard
          icon={Group}
          title="Categorização de Publicadores"
          tooltip='Associe perfis de redes sociais a grupos (ex: "Políticos", "Mídia Nacional").'
          description={getDescription(company.counts?.publisherPersons)}
        />
      : <ManualCard
          icon={Group}
          title="Categorização de Publicadores"
          tooltip='Associe perfis de redes sociais a grupos (ex: "Políticos", "Mídia Nacional").'
          description={getDescription(company.counts?.publisherPersons)}
          linkName="Editar"
          linkAction={() =>
            changeQueryParam({
              param: 'modal',
              value: 'publisher-persons',
              replaceAll: true,
            })
          }
        />
      }

      <Modal
        controlKey="publisher-persons"
        backToName={company.name}
        title="Categorização de Publicadores"
        subtitle={<span>Importe uma planilha ou cadastre manualmente os perfis e seus grupos.</span>}
        icon={Group}
        beforeClose={reloadCompany}>
        <Flex $type="column" $align="stretch" $gap={24} $fit>
          <Flex $type="row" $justify="flex-end" $fit>
            <Button.Root $variant="primary" onClick={() => changeQueryParam({ param: 'addPerson', value: 'true' })}>
              <Button.Icon value={Plus} />
              <Button.Text value="Adicionar pessoa" />
            </Button.Root>
          </Flex>

          <PublisherPersonsTable />
        </Flex>
      </Modal>
      <PublisherPersonsFormModal
        excelFile={excelFile}
        setExcelFile={setExcelFile}
        onImport={handleImport}
        isImporting={isImporting}
      />
    </>
  )
}
